// NextAction is now referenced by ID only - no direct import needed
import { ActionContext } from '../value-objects/ActionContext';
import { EnergyLevel } from '../value-objects/EnergyLevel';
import { DomainEvent } from '../events/DomainEvent';
import { ProjectCompleted } from '../events/GTDEvents';
import { ProjectStateError, ThoughtProcessingError } from '../errors/GTDDomainError';

/**
 * Plan for creating a NextAction - returned by Project.planNextAction()
 * The actual NextAction aggregate is created by the application service
 */
export interface NextActionPlan {
  id: string;
  description: string;
  context: ActionContext;
  energy: EnergyLevel;
  minutes: number;
  createdBy: string;
  projectId: string;
}

/**
 * GTD Project - any outcome requiring more than one action step
 * "A project is any desired result that requires more than one action step" - David Allen
 * 
 * Core GTD Principles:
 * - Every project must have at least one next action
 * - Projects need regular review to stay on track
 * - Clear desired outcome is essential
 */
export class Project {
  private domainEvents: DomainEvent[] = [];
  private _nextActionIds: string[] = [];
  private _completedAt?: Date;
  private _status: ProjectStatus = ProjectStatus.ACTIVE;

  constructor(
    public readonly id: string,
    public readonly name: string,
    public readonly desiredOutcome: string,
    public readonly createdBy: string,
    public readonly createdAt: Date = new Date(),
    public readonly area?: string,
    public readonly reviewDate?: Date
  ) {
    this.validateProject(name, desiredOutcome);
  }

  /**
   * Create a new project with validation
   */
  static create(
    name: string,
    outcome: string,
    createdBy: string,
    area?: string,
    reviewDate?: Date
  ): Project {
    const id = this.generateId();
    return new Project(id, name, outcome, createdBy, new Date(), area, reviewDate);
  }

  /**
   * Plan next action for this project
   * GTD Rule: Every project must have at least one next action
   * Returns action data to be created by application service
   */
  planNextAction(
    description: string,
    context: ActionContext,
    energy: EnergyLevel,
    minutes: number
  ): NextActionPlan {
    if (this.isCompleted() || this.isCancelled()) {
      throw new ProjectStateError(
        'Cannot add actions to completed or cancelled projects',
        this.id,
        this._status
      );
    }

    // Generate ID for the planned action
    const actionId = this.generateActionId();
    
    return {
      id: actionId,
      description,
      context,
      energy,
      minutes,
      createdBy: this.createdBy,
      projectId: this.id
    };
  }

  /**
   * Record that a next action has been created for this project
   */
  recordNextActionCreated(actionId: string): void {
    if (!this._nextActionIds.includes(actionId)) {
      this._nextActionIds.push(actionId);
    }
  }

  /**
   * Record that an action within this project has been completed
   * The actual NextAction completion is handled by NextAction aggregate
   */
  recordActionCompleted(actionId: string): void {
    if (!this._nextActionIds.includes(actionId)) {
      throw new ProjectStateError(
        'Action not found in this project',
        this.id,
        this._status
      );
    }
    // Action completion is handled by the NextAction aggregate itself
    // This method just validates the action belongs to this project
  }

  /**
   * Remove a next action from this project
   */
  removeAction(actionId: string): void {
    const actionIndex = this._nextActionIds.findIndex(id => id === actionId);
    if (actionIndex === -1) {
      throw new ProjectStateError(
        'Action not found in this project',
        this.id,
        this._status
      );
    }

    this._nextActionIds.splice(actionIndex, 1);
  }

  /**
   * Complete the entire project
   */
  complete(completedBy?: string, completionNotes?: string): void {
    if (this.isCompleted()) {
      throw new ProjectStateError(
        'Project is already completed',
        this.id,
        this._status
      );
    }

    this._status = ProjectStatus.COMPLETED;
    this._completedAt = new Date();
    
    this.addDomainEvent(new ProjectCompleted(
      this.id,
      completedBy || this.createdBy,
      completionNotes
    ));
  }

  /**
   * Move to Someday/Maybe list (GTD workflow)
   */
  defer(): void {
    if (this.isCompleted()) {
      throw new ProjectStateError(
        'Cannot defer completed projects',
        this.id,
        this._status
      );
    }

    this._status = ProjectStatus.SOMEDAY_MAYBE;
  }

  /**
   * Reactivate from Someday/Maybe
   */
  activate(): void {
    if (this.isCompleted()) {
      throw new ProjectStateError(
        'Cannot activate completed projects',
        this.id,
        this._status
      );
    }

    this._status = ProjectStatus.ACTIVE;
  }

  /**
   * Cancel the project
   */
  cancel(): void {
    if (this.isCompleted()) {
      throw new ProjectStateError(
        'Cannot cancel completed projects',
        this.id,
        this._status
      );
    }

    this._status = ProjectStatus.CANCELLED;
  }

  /**
   * Get IDs of next actions for this project
   * Context and energy filtering should be done at application service level
   */
  getNextActionIds(): string[] {
    return [...this._nextActionIds];
  }

  /**
   * GTD Review: Check if project needs attention
   * A project needs attention if it has no next actions defined
   */
  needsAttention(): boolean {
    if (this._status !== ProjectStatus.ACTIVE) return false;
    
    // Project needs attention if it has no next actions at all
    return this._nextActionIds.length === 0;
  }

  /**
   * Get basic project metrics (action counts must be computed at application layer)
   */
  getBasicProgress(): { totalActionIds: number } {
    return {
      totalActionIds: this._nextActionIds.length
    };
  }

  // State queries
  isCompleted(): boolean {
    return this._status === ProjectStatus.COMPLETED;
  }

  isCancelled(): boolean {
    return this._status === ProjectStatus.CANCELLED;
  }

  isActive(): boolean {
    return this._status === ProjectStatus.ACTIVE;
  }

  isSomedayMaybe(): boolean {
    return this._status === ProjectStatus.SOMEDAY_MAYBE;
  }

  // Getters
  get status(): ProjectStatus {
    return this._status;
  }

  get nextActionIds(): string[] {
    return [...this._nextActionIds];
  }

  get completedAt(): Date | undefined {
    return this._completedAt;
  }

  // Event sourcing support
  getDomainEvents(): DomainEvent[] {
    // Only return events from this aggregate
    // NextAction events are handled by NextAction aggregates separately
    return [...this.domainEvents];
  }

  clearDomainEvents(): void {
    this.domainEvents = [];
    // NextAction events are cleared by NextAction aggregates separately
  }

  // Private methods
  private validateProject(name: string, desiredOutcome: string): void {
    if (!name?.trim()) {
      throw new ThoughtProcessingError('Project name is required');
    }

    if (!desiredOutcome?.trim()) {
      throw new ThoughtProcessingError('Desired outcome is required for project clarity');
    }
  }

  private addDomainEvent(event: DomainEvent): void {
    this.domainEvents.push(event);
  }

  private static generateId(): string {
    return `project-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  }

  private generateActionId(): string {
    return `action-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  }
}

/**
 * Project Status following GTD workflow
 */
export enum ProjectStatus {
  ACTIVE = 'active',
  SOMEDAY_MAYBE = 'someday-maybe',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled'
}

/**
 * Project progress metrics
 */
export interface ProjectProgress {
  totalActions: number;
  completedActions: number;
  assignedActions: number;
  availableActions: number;
  completionPercentage: number;
}